import React from 'react';
import { useTheme } from './ThemeContext';

const getFeedbackMessage = (interval) => {
    const deviation = interval - 1000;
    if (Math.abs(deviation) <= 50) return "PERFECT! 🎯";
    if (Math.abs(deviation) <= 200) {
        return deviation > 0 ? "Good, but late 👍" : "Good, but early 👍";
    }
    return deviation > 0 ? "Too late! 🕐" : "Too soon! 🏃";
};

const getFeedbackColor = (interval) => {
    const deviation = Math.abs(interval - 1000);
    if (deviation <= 50) return '#10B981';
    if (deviation <= 200) return '#F59E0B';
    return '#EF4444';
};

const ProgressCircles = ({ clickCount, timeArray, selectedTimeHorizon, isSoloMode, isInModal = false }) => {
    const { theme, isAscii: isAnyAscii } = useTheme();
    const isDarkTheme = theme === 'dark';

    // Compute feedback directly in render — no useState/useEffect needed,
    // so text and color are always in sync with no extra render cycle.
    let feedback = 'Click to start!';
    let feedbackColor = '#4A5568';
    if (isSoloMode && !isInModal && clickCount > 1 && clickCount < selectedTimeHorizon) {
        const interval = timeArray[clickCount - 1] - timeArray[clickCount - 2];
        feedback = getFeedbackMessage(interval);
        feedbackColor = getFeedbackColor(interval);
    }

    const getCircleStyle = (index) => {
        const isDark = isDarkTheme;
        const baseStyle = {
            aspectRatio: '1 / 1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: `2px solid ${isDark ? '#4A5568' : '#CBD5E0'}`,
            transition: 'background-color 0.1s ease',
            backgroundColor: isDark ? '#1A202C' : '#FFFFFF',
            color: index < clickCount ? '#FFFFFF' : (isDark ? '#A0AEC0' : '#4A5568'),
            fontSize: '0.875rem',
            fontWeight: '600',
            boxSizing: 'border-box',
        };

        if (index >= clickCount) {
            return baseStyle;
        }

        if (!isSoloMode && !isInModal) {
            // In competitive mode (not in modal), all clicked circles are blue
            return {
                ...baseStyle,
                backgroundColor: '#3B82F6'
            };
        }

        // Solo mode or modal coloring logic
        if (index === 0) {
            return {
                ...baseStyle,
                backgroundColor: '#3B82F6'
            };
        }

        const interval = timeArray[index] - timeArray[index - 1];
        const deviation = Math.abs(interval - 1000);

        if (deviation <= 50) {
            return {
                ...baseStyle,
                backgroundColor: '#10B981'
            };
        }
        if (deviation <= 200) {
            return {
                ...baseStyle,
                backgroundColor: '#F59E0B'
            };
        }
        return {
            ...baseStyle,
            backgroundColor: '#EF4444'
        };
    };

    if (isAnyAscii) {
        const isAsciiDark = isAnyAscii && isDarkTheme;
        const baseColor = isAsciiDark ? '#33ff33' : '#1a1a1a';
        const dimColor = isAsciiDark ? 'rgba(51,255,51,0.25)' : 'rgba(0,0,0,0.2)';
        const getAsciiDotColor = (index) => {
            if (index >= clickCount) return dimColor;
            const style = getCircleStyle(index);
            return style.backgroundColor || baseColor;
        };
        return (
            <div style={{ fontFamily: '"Courier New", Courier, monospace', color: baseColor, padding: '0.5rem 0', width: '100%' }}>
                {isSoloMode && !isInModal && (
                    <div style={{ color: feedbackColor, fontSize: '1rem', textAlign: 'center', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {feedback}
                    </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                    {Array.from({ length: selectedTimeHorizon }).map((_, index) => (
                        <span key={index} style={{ color: getAsciiDotColor(index), fontSize: '1.4rem', lineHeight: 1 }}>
                            {index < clickCount ? '●' : '○'}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full">
            {/* Feedback Message - Only show in solo mode and not in modal */}
            {isSoloMode && !isInModal && (
                <div
                    className="absolute w-full text-center -top-12"
                    style={{
                        color: feedbackColor,
                        fontSize: '1.25rem',
                        fontWeight: 'bold'
                    }}
                >
                    {feedback}
                </div>
            )}

            {/* Circles Container — always 10 per row; min() caps at 32px in wide modal, fills proportionally on mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, min(calc(10% - 0.9px), 32px))', gap: '1px', width: '100%', justifyContent: 'center', padding: '0.5rem 0' }}>
                {Array.from({ length: selectedTimeHorizon }).map((_, index) => (
                    <div
                        key={index}
                        style={getCircleStyle(index)}
                    >
                        {index + 1}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProgressCircles;
